import { FC, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PageContent from '../../layouts/PageContent/PageContent';
import { useDashboard } from '../../api/hooks/useDashboard';
import { useGetDashboards } from '../../api/hooks/useGetDashboards';
import { useDashboards } from '../../hooks/useDashboards';
import { useGlobalFilterValues } from '../../hooks/useGlobalFilterValues';
import { useUserState } from '../../hooks/context/useUserState';
import Dashboard from '../../components/Dashboard/Dashboard';
import { SET_GLOBAL_FILTER_VALUES } from '../../constants';
import FilterForm, { FilterData } from '../../components/FilterForm/FilterForm';
import ReportsLoading from '../../components/ReportsLoading/ReportsLoading';
import ApiError from '../../components/ApiError/ApiError';
import { getFiltersButtonText } from '../../utils/filters/getFiltersButtonText';

const Dashboards: FC = () => {
  const [dashboardReady, setDashboardReady] = useState(false);

  const {
    isLoading: dashboardsLoading,
    error,
    fetch: fetchDashboards,
  } = useGetDashboards();
  const {
    isLoading: dashboardLoading,
    fetch: fetchDashboard,
    error: dashboardError,
  } = useDashboard();
  const dashboards = useDashboards();
  const {
    state: { autologoutPending },
    dispatch,
  } = useUserState();
  const globalFilterValues = useGlobalFilterValues();

  const dashboardFilters =
    !dashboardLoading && dashboards && dashboards[0]?.filters
      ? dashboards[0].filters
      : null;
  const filtersButtonText = getFiltersButtonText(
    dashboardFilters,
    globalFilterValues
  );
  const apiError = error || dashboardError;
  const isLoading = dashboardsLoading || dashboardLoading;
  const dashboard = dashboards?.[0];
  const showLoader = !apiError && (isLoading || !dashboard || !dashboardReady);

  const handleGlobalFilterSubmit = (values: FilterData[]) => {
    setDashboardReady(false);
    dispatch({
      type: SET_GLOBAL_FILTER_VALUES,
      filterData: values,
    });
  };

  const handleReadyChange = useCallback((ready: boolean) => {
    setDashboardReady(ready);
  }, []);

  // The Header lives in the persistent shell; portal the dashboard filter into its slot.
  const [filterSlot, setFilterSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setFilterSlot(document.querySelector<HTMLElement>('.header-filter-slot'));
  }, []);

  useEffect(() => {
    if (autologoutPending) return;
    const load = async () => {
      const fetched = await fetchDashboards();
      const first = fetched?.[0];
      if (!first) return;
      setDashboardReady(false);
      await fetchDashboard(first.id);
    };
    load();
  }, []);

  return (
    <PageContent>
      {filterSlot &&
        dashboardFilters &&
        dashboardFilters.length !== 0 &&
        createPortal(
          <FilterForm
            onSubmit={handleGlobalFilterSubmit}
            filters={dashboardFilters ?? undefined}
            values={globalFilterValues}
            popover
            buttonText={filtersButtonText}
          />,
          filterSlot
        )}
      {apiError && <ApiError error={apiError} />}
      {!apiError && showLoader && <ReportsLoading />}
      {!apiError && !isLoading && dashboard && (
        <div style={{ display: dashboardReady ? 'contents' : 'none' }}>
          <Dashboard id={dashboard.id} onReadyChange={handleReadyChange} />
        </div>
      )}
    </PageContent>
  );
};

export default Dashboards;
